import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import FormikTextField from 'components/Fields/FormikTextField'
import { Form, Formik, FormikHelpers } from 'formik'
import React from 'react'
import { apiStatus } from 'shared/constants/AppEnum'
import { IApiState } from 'store/types'
import { IOfferingResponse, StaffProfilePayloadType, StaffProfileResponseType, StaffProfileRoleType, UsersResponseType } from 'types/api/temple'
import { extractError, isFieldError } from 'utils/api'
import * as yup from 'yup'

type StaffProfileFormType = StaffProfilePayloadType & {
    languageText: string
}

interface FormStaffProfileProps {
    initialValues?: StaffProfileResponseType
    users: UsersResponseType[]
    profiles: StaffProfileResponseType[]
    offerings: IOfferingResponse[]
    buttonLabel: string
    api: IApiState
    onSubmit: (values: StaffProfilePayloadType, helpers: FormikHelpers<StaffProfileFormType>) => void | Promise<any>
}

const profileToForm = (profile?: StaffProfileResponseType): StaffProfileFormType => ({
    templeAdmin: profile?.templeAdmin ?? '',
    roleType: profile?.roleType ?? 'staff',
    priestType: profile?.priestType ?? null,
    languages: profile?.languages ?? [],
    languageText: profile?.languages?.join(', ') ?? '',
    excludedOfferings: profile?.excludedOfferings ?? [],
    notes: profile?.notes ?? '',
    isActive: profile?.isActive ?? true,
})

const toPayload = (values: StaffProfileFormType): StaffProfilePayloadType => ({
    templeAdmin: values.templeAdmin,
    roleType: values.roleType,
    priestType: values.roleType === 'priest' ? values.priestType || null : null,
    languages: values.languageText
        .split(',')
        .map(language => language.trim().toLowerCase())
        .filter(Boolean),
    excludedOfferings: values.roleType === 'priest' ? values.excludedOfferings : [],
    notes: values.notes,
    isActive: values.isActive,
})

const roleOptions: { value: StaffProfileRoleType, label: string }[] = [
    { value: 'staff', label: 'Staff' },
    { value: 'priest', label: 'Priest' },
    { value: 'other', label: 'Other' },
]

const priestTypeOptions = [
    { value: 'vaishnava', label: 'Vaishnava' },
    { value: 'shiva', label: 'Shiva' },
    { value: 'other', label: 'Other' },
]

const FormStaffProfile: React.FC<FormStaffProfileProps> = ({
    initialValues,
    users,
    profiles,
    offerings,
    buttonLabel,
    api,
    onSubmit,
}) => {
    const profiledTempleAdminIds = profiles
        .filter(profile => !initialValues || profile.id !== initialValues.id)
        .map(profile => String(profile.templeAdmin))

    const validationSchema = yup.object({
        templeAdmin: yup.string().required('Linked user is required'),
        roleType: yup.string().required('Profile type is required'),
    })

    return (
        <Formik
            enableReinitialize
            initialValues={profileToForm(initialValues)}
            validationSchema={validationSchema}
            onSubmit={(values, helpers) => onSubmit(toPayload(values), helpers)}
        >
            {({ dirty, errors, isSubmitting, isValid, setFieldValue, touched, values }) => (
                <Form noValidate autoComplete='off'>
                    <Grid container spacing={6} direction='column'>
                        {api.status === apiStatus.FAILED && !isFieldError(api.error) && (
                            <Grid item>
                                <Alert severity='error'>{extractError(api.error)}</Alert>
                            </Grid>
                        )}
                        <Grid item>
                            <FormControl variant='outlined' fullWidth required error={Boolean(touched.templeAdmin && errors.templeAdmin)}>
                                <InputLabel id='staff-profile-user-label'>Linked User</InputLabel>
                                <Select
                                    labelId='staff-profile-user-label'
                                    label='Linked User'
                                    value={values.templeAdmin}
                                    disabled={Boolean(initialValues)}
                                    onChange={event => setFieldValue('templeAdmin', event.target.value)}
                                >
                                    {users.map(user => {
                                        const disabled = profiledTempleAdminIds.includes(String(user.id))
                                        return (
                                            <MenuItem key={user.id} value={user.id} disabled={disabled}>
                                                {user.user.fullName} ({user.role})
                                            </MenuItem>
                                        )
                                    })}
                                </Select>
                                {touched.templeAdmin && errors.templeAdmin && <FormHelperText>{errors.templeAdmin}</FormHelperText>}
                            </FormControl>
                        </Grid>
                        <Grid item container spacing={6}>
                            <Grid item xs={12} md={6}>
                                <FormControl variant='outlined' fullWidth required>
                                    <InputLabel id='staff-profile-role-label'>Profile Type</InputLabel>
                                    <Select
                                        labelId='staff-profile-role-label'
                                        label='Profile Type'
                                        value={values.roleType}
                                        onChange={event => {
                                            setFieldValue('roleType', event.target.value)
                                            if (event.target.value !== 'priest') {
                                                setFieldValue('priestType', null)
                                                setFieldValue('excludedOfferings', [])
                                            }
                                        }}
                                    >
                                        {roleOptions.map(option => (
                                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl variant='outlined' fullWidth disabled={values.roleType !== 'priest'}>
                                    <InputLabel id='staff-priest-type-label'>Priest Type</InputLabel>
                                    <Select
                                        labelId='staff-priest-type-label'
                                        label='Priest Type'
                                        value={values.priestType ?? ''}
                                        onChange={event => setFieldValue('priestType', event.target.value || null)}
                                    >
                                        <MenuItem value=''>None</MenuItem>
                                        {priestTypeOptions.map(option => (
                                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <FormikTextField
                                name='languageText'
                                label='Languages'
                                placeholder='malayalam, sanskrit, english'
                                variant='outlined'
                            />
                        </Grid>
                        <Grid item>
                            <FormControl variant='outlined' fullWidth disabled={values.roleType !== 'priest'}>
                                <InputLabel id='staff-excluded-offerings-label'>Excluded Offerings</InputLabel>
                                <Select
                                    labelId='staff-excluded-offerings-label'
                                    multiple
                                    label='Excluded Offerings'
                                    value={values.excludedOfferings}
                                    onChange={event => setFieldValue('excludedOfferings', event.target.value)}
                                    renderValue={selected => (
                                        <Box component='span'>
                                            {(selected as (string | number)[])
                                                .map(id => offerings.find(offering => offering.id === id)?.name)
                                                .filter(Boolean)
                                                .join(', ')}
                                        </Box>
                                    )}
                                >
                                    {offerings.map(offering => (
                                        <MenuItem key={offering.id} value={offering.id}>
                                            <Checkbox checked={values.excludedOfferings.indexOf(offering.id) > -1} />
                                            <ListItemText primary={offering.name} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item>
                            <FormikTextField
                                name='notes'
                                label='Notes'
                                placeholder='Internal notes'
                                variant='outlined'
                                multiline
                                rows={4}
                            />
                        </Grid>
                        <Grid item>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color='primary'
                                        checked={values.isActive}
                                        onChange={event => setFieldValue('isActive', event.target.checked)}
                                    />
                                }
                                label='Active'
                            />
                        </Grid>
                        <Grid item>
                            <Button
                                color='primary'
                                variant='contained'
                                type='submit'
                                disabled={isSubmitting || !dirty || !isValid || api.status === apiStatus.LOADING}
                            >
                                {buttonLabel}
                            </Button>
                        </Grid>
                    </Grid>
                </Form>
            )}
        </Formik>
    )
}

export default FormStaffProfile
